import React, { useState } from "react"
import { submitGuess } from "./justOneRoom"
import { Screen, JoButton, PlayerDot, C, F } from "./JustOneUI"
import { playSound } from "../../../utils/ballparkSounds"

const GuessScreen = ({ room, code, playerId }) => {
  const [guess, setGuess] = useState("")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState("")

  const isGuesser = room.guesserId === playerId
  const guesserName = room.players?.[room.guesserId]?.name || "The Guesser"
  const secretWord = room.secretWord || ""

  const players = Object.entries(room.players || {}).sort(
    (a, b) => a[1].joinedAt - b[1].joinedAt
  )

  const clueGivers = players.filter(([pid]) => pid !== room.guesserId)
  
  // Calculate remaining unique clues
  const activeClues = clueGivers.filter(([pid]) => !room.eliminatedClues?.[pid])

  const handleSubmitGuess = async (isPass = false) => {
    if (!isPass && !guess.trim()) return

    setBusy(true)
    setError("")
    try {
      playSound(isPass ? "click" : "click")
      await submitGuess(code, room.round, isPass ? "" : guess.trim(), isPass)
    } catch (e) {
      setError("Failed to submit guess. Try again.")
      setBusy(false)
    }
  }

  return (
    <Screen style={{ gap: 16 }}>
      {/* Header bar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${C.border}`, paddingBottom: 10 }}>
        <div style={{ fontFamily: F.display, fontWeight: 900, fontSize: 16, color: C.muted }}>
          ROUND {room.round} OF {room.totalRounds}
        </div>
        <div style={{ fontFamily: F.display, fontWeight: 900, fontSize: 16, color: C.teal }}>
          TEAM SCORE: {room.teamScore || 0}
        </div>
      </div>

      {isGuesser ? (
        // GUESSER GUESS SCREEN
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 18 }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: 1.5, color: C.muted, fontFamily: F.display, marginBottom: 4 }}>
              YOUR CLUES ARE
            </div>
          </div>

          {activeClues.length === 0 ? (
            <div style={{ padding: "30px 20px", textAlign: "center", background: "#FEF2F2", border: `2px dashed ${C.coral}`, borderRadius: 20 }}>
              <div style={{ fontSize: 44, marginBottom: 8 }}>😱</div>
              <div style={{ fontFamily: F.display, fontWeight: 900, fontSize: 20, color: C.coral, marginBottom: 4 }}>
                All Clues Eliminated!
              </div>
              <div style={{ fontSize: 14, color: C.muted, fontWeight: 700 }}>
                Every single clue giver submitted a duplicate or invalid clue! You have zero clues to go on.
              </div>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
              {activeClues.map(([pid, p]) => (
                <div
                  key={pid}
                  style={{
                    background: C.card,
                    border: `2px solid ${p.color}`,
                    borderRadius: 16,
                    padding: "16px 12px",
                    textAlign: "center",
                    boxShadow: "0 4px 6px rgba(0,0,0,0.02)",
                    position: "relative",
                  }}
                >
                  <div style={{ position: "absolute", top: 8, left: 8 }}>
                    <PlayerDot color={p.color} size={14} />
                  </div>
                  <div
                    style={{
                      fontFamily: F.display,
                      fontWeight: 900,
                      fontSize: 22,
                      color: C.ink,
                      textTransform: "uppercase",
                      marginTop: 4,
                      wordBreak: "break-word",
                    }}
                  >
                    {room.clues?.[pid] || ""}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={{ fontSize: 12, fontWeight: 800, color: C.muted, letterSpacing: 0.5, paddingLeft: 4 }}>
                YOUR GUESS
              </label>
              <input
                style={{
                  height: 52,
                  borderRadius: 12,
                  border: `1.5px solid ${C.borderDark}`,
                  background: C.card,
                  padding: "0 16px",
                  fontSize: 18,
                  fontWeight: 700,
                  fontFamily: F.body,
                  color: C.ink,
                  width: "100%",
                  boxSizing: "border-box",
                  outline: "none",
                }}
                placeholder="What is the secret word?"
                value={guess}
                maxLength={24}
                onChange={(e) => {
                  setGuess(e.target.value)
                  setError("")
                }}
                disabled={busy}
              />
            </div>

            {error && (
              <div style={{ color: C.coral, fontWeight: 700, fontSize: 14, textAlign: "center" }}>
                {error}
              </div>
            )}

            <div style={{ display: "flex", gap: 12 }}>
              <JoButton
                variant="cyan"
                onClick={() => handleSubmitGuess(false)}
                disabled={busy || !guess.trim()}
                style={{ flex: 2 }}
              >
                {busy ? "Submitting…" : "Guess"}
              </JoButton>
              <JoButton
                variant="panel"
                onClick={() => handleSubmitGuess(true)}
                disabled={busy}
                style={{ flex: 1, color: C.coral }}
              >
                Pass
              </JoButton>
            </div>
          </div>
        </div>
      ) : (
        // CLUE GIVERS WATCHING SCREEN
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 18 }}>
          <div style={{ textAlign: "center", padding: "12px", background: C.highlight, border: `1.5px solid ${C.cyan}`, borderRadius: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: C.cyanDark, fontFamily: F.display }}>
              SECRET WORD: <span style={{ textTransform: "uppercase", fontSize: 18, color: C.ink, marginLeft: 4 }}>{secretWord}</span>
            </div>
          </div>

          <div style={{ textAlign: "center", padding: "16px 0" }}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                border: `3px solid ${C.teal}`,
                borderTopColor: "transparent",
                animation: "spin 1s linear infinite",
                margin: "0 auto 12px",
              }}
            />
            <div style={{ fontFamily: F.display, fontWeight: 800, fontSize: 20, color: C.ink }}>
              {guesserName} is guessing!
            </div>
            <div style={{ fontSize: 14, color: C.muted, fontWeight: 700, marginTop: 4 }}>
              They are viewing the unique clues.
            </div>
          </div>

          <div>
            <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1.5, color: C.muted, fontFamily: F.display, marginBottom: 8 }}>
              CLUES VISIBLE TO {guesserName.toUpperCase()}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {clueGivers.map(([pid, p]) => {
                const isEliminated = !!room.eliminatedClues?.[pid]
                const rawClue = room.clues?.[pid] || ""

                return (
                  <div
                    key={pid}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      background: isEliminated ? C.panel : C.card,
                      border: `1.5px solid ${isEliminated ? C.border : p.color}`,
                      borderRadius: 12,
                      padding: "8px 12px",
                      opacity: isEliminated ? 0.45 : 1,
                    }}
                  >
                    <PlayerDot color={p.color} size={16} />
                    <span style={{ fontWeight: 800, fontSize: 14, color: C.ink }}>{p.name}</span>
                    <span
                      style={{
                        fontWeight: 900,
                        fontSize: 16,
                        color: isEliminated ? C.muted : C.ink,
                        textTransform: "uppercase",
                        textDecoration: isEliminated ? "line-through" : "none",
                        marginLeft: 12,
                      }}
                    >
                      {rawClue}
                    </span>
                    {isEliminated && (
                      <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 800, color: C.coral }}>
                        ELIMINATED
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}
    </Screen>
  )
}

export default GuessScreen
